/**
 * NotificationBell — header notification inbox with poll, per-item read,
 * and a tap-to-read detail view.
 * Extracted from Header.js (Phase 3 refactor, 2026-05-03).
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Star, Flame, Trophy, Target, Sparkles, Gamepad2, Book, CheckCheck, X,
  ChevronLeft, ArrowRight, Gift, Crown, Check, Trash2, Moon,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatNotifWhen, relativeTime } from "../../lib/timeFormat";
import { isQuietHoursNow, soundsMutedNow, muteReason, snoozeSound, clearSnooze, snoozeUntil, getNotifSettings, setNotifSettings } from "../../utils/notificationSound";
import { BellOff, Volume2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Voice notifications open the walkie-talkie popup (never auto-shown) instead
// of a text detail view.
// Which "Today / Yesterday / Earlier" bucket a notification falls into, judged
// in the user's own timezone so day boundaries feel right.
const dayBucket = (iso, tz) => {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const opts = { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" };
  try {
    const key = (x) => new Intl.DateTimeFormat("en-CA", opts).format(x); // YYYY-MM-DD
    const now = new Date();
    const yest = new Date(now.getTime() - 86400000);
    const dk = key(d);
    if (dk === key(now)) return "Today";
    if (dk === key(yest)) return "Yesterday";
    return "Earlier";
  } catch {
    return "Earlier";
  }
};

const VOICE_NOTIF_TYPES = new Set(["friend_voice", "friend_talk"]);

const NOTIF_ICONS = {
  flame: Flame, trophy: Trophy, target: Target, sparkles: Sparkles,
  star: Star, bell: Bell, gamepad: Gamepad2, book: Book, gift: Gift, crown: Crown,
};

// Some notifications (streak / level / quest / welcome) are generated on the
// fly server-side and never stored, so the server read endpoint can't persist
// their read state. We remember those locally per browser so tapping one keeps
// it read across refreshes.
const LS_READ_KEY = "iw_read_notifs";
const getLocalReadIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LS_READ_KEY) || "[]")); }
  catch { return new Set(); }
};
const addLocalReadId = (id) => {
  try {
    const s = getLocalReadIds(); s.add(id);
    localStorage.setItem(LS_READ_KEY, JSON.stringify([...s].slice(-200)));
  } catch { /* storage unavailable */ }
};

// Locally-deleted ids. Synthetic notifications (streak/quest/etc.) aren't
// stored server-side, so deleting them is remembered per-browser; stored ones
// are also deleted on the server.
const LS_DEL_KEY = "iw_deleted_notifs";
const getLocalDeletedIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LS_DEL_KEY) || "[]")); }
  catch { return new Set(); }
};
const addLocalDeletedIds = (ids) => {
  try {
    const s = getLocalDeletedIds(); ids.forEach((id) => s.add(id));
    localStorage.setItem(LS_DEL_KEY, JSON.stringify([...s].slice(-400)));
  } catch { /* storage unavailable */ }
};

export const NotificationBell = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selected, setSelected] = useState(null); // the notification being read
  const [selectMode, setSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [expandedTimes, setExpandedTimes] = useState(() => new Set()); // notif ids showing the full absolute time
  const [quietActive, setQuietActive] = useState(() => isQuietHoursNow());
  const [muted, setMuted] = useState(() => soundsMutedNow());
  const [muteReasonVal, setMuteReasonVal] = useState(() => muteReason());
  const [snoozeEnd, setSnoozeEnd] = useState(() => snoozeUntil());
  const [snoozeMenu, setSnoozeMenu] = useState(false);

  // Keep the quiet-hours / snooze moon badge + status in sync: re-check on a
  // minute tick and whenever the user changes their sound/Quiet Hours settings.
  useEffect(() => {
    const check = () => {
      setQuietActive(isQuietHoursNow());
      setMuted(soundsMutedNow());
      setMuteReasonVal(muteReason());
      setSnoozeEnd(snoozeUntil());
    };
    check();
    const id = setInterval(check, 30000);
    window.addEventListener("iw:notif-settings", check);
    return () => { clearInterval(id); window.removeEventListener("iw:notif-settings", check); };
  }, []);

  const doSnooze = (hours) => { snoozeSound(hours); setSnoozeMenu(false); };
  const resumeSounds = () => {
    clearSnooze();
    // If sounds were turned fully off, flip them back on when the user resumes.
    if (!getNotifSettings().soundEnabled) setNotifSettings({ soundEnabled: true });
  };
  const pendingDelRef = useRef(new Set()); // ids hidden during an Undo window
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    if (!user || user.isDemo) return;
    try {
      const uid = user.id || user.email;
      const r = await axios.get(`${API}/api/notifications/user/${uid}`);
      const localRead = getLocalReadIds();
      const localDeleted = getLocalDeletedIds();
      const list = (r.data.notifications || [])
        .filter((n) => !localDeleted.has(n.id) && !pendingDelRef.current.has(n.id))
        .map((n) => (localRead.has(n.id) ? { ...n, read: true } : n));
      setNotifications(list);
      setUnreadCount(list.filter((n) => !n.read).length);
    } catch (e) { /* silent */ }
  }, [user]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Poll every 15s as a fallback; the live socket updates instantly via event.
  useEffect(() => {
    if (!user || user.isDemo) return;
    const interval = setInterval(fetchNotifications, 15000);
    const onEvent = () => fetchNotifications();
    window.addEventListener("iw:social-event", onEvent);
    return () => { clearInterval(interval); window.removeEventListener("iw:social-event", onEvent); };
  }, [user, fetchNotifications]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSelected(null); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const uid = user && (user.id || user.email);
  // The user's timezone for stamping notifications. Falls back to US Eastern
  // when they haven't set one in their profile (default for the US East Coast).
  const userTz = (user && (user.timezone || user.time_zone))
    || (() => { try { return localStorage.getItem("iw_user_tz"); } catch { return null; } })()
    || "America/New_York";

  const markOneRead = useCallback((notif) => {
    if (!notif || notif.read) return;
    addLocalReadId(notif.id);
    setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    // Best-effort persist for stored notifications (no-op for synthetic ones).
    axios.post(`${API}/api/notifications/user/${uid}/read/${notif.id}`).catch(() => {});
  }, [uid]);

  const markAllRead = async () => {
    setNotifications((prev) => {
      prev.forEach((n) => { if (!n.read) addLocalReadId(n.id); });
      return prev.map((n) => ({ ...n, read: true }));
    });
    setUnreadCount(0);
    try { await axios.post(`${API}/api/notifications/user/${uid}/read-all`); } catch (e) { /* silent */ }
  };

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const shownNotifs = notifications.slice(0, 15);
  const allChecked = shownNotifs.length > 0 && shownNotifs.every((n) => checkedIds.has(n.id));
  const toggleSelectAll = () => {
    setCheckedIds(allChecked ? new Set() : new Set(shownNotifs.map((n) => n.id)));
  };
  const exitSelectMode = () => { setSelectMode(false); setCheckedIds(new Set()); };

  // Delete with a 5s Undo (mirrors chat message delete): hide immediately, keep
  // hidden through the poll, and only commit (server delete + local-hide) once
  // the window lapses. Undo restores everything from the server (correct order).
  const commitDelete = (ids) => {
    ids.forEach((id) => pendingDelRef.current.delete(id));
    addLocalDeletedIds(ids);
    axios.post(`${API}/api/notifications/user/${uid}/delete`, { ids }).catch(() => {});
  };
  const runDelete = (ids, removed) => {
    if (!ids.length) return;
    ids.forEach((id) => pendingDelRef.current.add(id));
    setNotifications((prev) => prev.filter((n) => !ids.includes(n.id)));
    setUnreadCount((c) => Math.max(0, c - removed.filter((n) => !n.read).length));
    exitSelectMode();
    let undone = false;
    const timer = setTimeout(() => { if (!undone) commitDelete(ids); }, 5000);
    const n = ids.length;
    toast(`${n} notification${n > 1 ? "s" : ""} deleted`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          undone = true;
          clearTimeout(timer);
          ids.forEach((id) => pendingDelRef.current.delete(id));
          fetchNotifications(); // restore from server in the correct order
        },
      },
    });
  };
  const deleteChecked = () => {
    const ids = [...checkedIds];
    runDelete(ids, notifications.filter((n) => checkedIds.has(n.id)));
  };
  const deleteAll = () => {
    const ids = notifications.map((n) => n.id);
    const removed = [...notifications];
    if (!ids.length) return;
    ids.forEach((id) => pendingDelRef.current.add(id));
    setNotifications([]);
    setUnreadCount(0);
    exitSelectMode();
    let undone = false;
    const timer = setTimeout(() => {
      if (undone) return;
      ids.forEach((id) => pendingDelRef.current.delete(id));
      addLocalDeletedIds(ids);
      axios.post(`${API}/api/notifications/user/${uid}/delete-all`).catch(() => {});
    }, 5000);
    toast("All notifications deleted", {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => { undone = true; clearTimeout(timer); ids.forEach((id) => pendingDelRef.current.delete(id)); fetchNotifications(); },
      },
    });
  };

  // Tapping a notification opens its full message and marks just that one read.
  // Gift notifications are special: instead of a detail view they pop open the
  // Gift panel (same place the FAB opens) so the kid can claim / say thanks.
  const openNotif = (notif) => {
    markOneRead(notif);
    if (notif.type === "gift") {
      window.dispatchEvent(new CustomEvent("iw-open-gift-inbox"));
      setOpen(false);
      setSelected(null);
      return;
    }
    // Voice notes: open the walkie-talkie popup for that friend (the popup is
    // never shown automatically — only here, on an explicit tap).
    if (VOICE_NOTIF_TYPES.has(notif.type) && notif.from_user_id) {
      window.dispatchEvent(new CustomEvent("iw:friend-talk", {
        detail: { id: notif.from_user_id, name: notif.from_name },
      }));
      setOpen(false);
      setSelected(null);
      return;
    }
    setSelected(notif);
  };

  const goToAction = (notif) => {
    if (notif?.type === "gift") {
      window.dispatchEvent(new CustomEvent("iw-open-gift-inbox"));
      setOpen(false);
      setSelected(null);
      return;
    }
    if (notif?.action_url) {
      // For chat/voice notifications, also fire the direct open-chat event so the
      // thread opens reliably even if we're already on /friends (a same-URL
      // navigate won't re-trigger the query-param effect) or the list is cold.
      const m = /[?&]chat=([^&]+)/.exec(notif.action_url);
      if (m) {
        const fid = notif.from_user_id || decodeURIComponent(m[1]);
        window.dispatchEvent(new CustomEvent("iw:open-friend-chat", { detail: { id: fid, name: notif.from_name } }));
      }
      navigate(notif.action_url);
      setOpen(false);
      setSelected(null);
    }
  };

  if (!user || user.isDemo) return null;

  const panelClass = `
    fixed left-2 right-2 top-[64px] z-50 overflow-hidden
    sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80
    max-h-[80vh] overflow-y-auto
    rounded-2xl border-2 border-white/15
    bg-slate-900 sm:bg-slate-900/98 backdrop-blur-md
    shadow-2xl shadow-black/60
  `;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); setSelected(null); if (!open) fetchNotifications(); }}
        className="relative p-1.5 rounded-lg text-white/50 hover:text-white/80 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={18} />
        {muted && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-indigo-500 border border-slate-900 flex items-center justify-center"
            title={muteReasonVal === "off" ? "Notification sounds are off" : muteReasonVal === "snooze" ? "Sounds snoozed" : "Quiet hours are on — sounds muted"}
            data-testid="quiet-hours-badge"
          >
            <Moon size={8} className="text-white" />
          </span>
        )}
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
            style={{ boxShadow: "0 0 8px rgba(239,68,68,0.5)" }}
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && !selected && (
        <div className={panelClass} style={{ backgroundColor: "rgba(15, 23, 42, 0.99)" }} data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <h3 className="text-white text-sm font-fredoka font-bold">Notifications</h3>
            <div className="flex items-center gap-3">
              {!selectMode && unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-blue-400/70 hover:text-blue-400 text-[10px] font-bold transition-colors"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
              {!selectMode && notifications.length > 0 && (
                <button
                  onClick={() => setSelectMode(true)}
                  className="text-white/50 hover:text-white text-[10px] font-bold transition-colors"
                  data-testid="notif-select-btn"
                >
                  Select
                </button>
              )}
              {selectMode && (
                <button
                  onClick={exitSelectMode}
                  className="text-white/50 hover:text-white text-[10px] font-bold transition-colors"
                  data-testid="notif-select-cancel"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => { setOpen(false); exitSelectMode(); }}
                aria-label="Close notifications"
                data-testid="notifications-close-btn"
                className="w-6 h-6 rounded-full bg-white/5 text-white/50 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Sound snooze / status bar */}
          {!selectMode && (
            <div className="relative px-4 py-2 border-b border-white/8 bg-white/[0.02]" data-testid="notif-sound-bar">
              {muted ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-indigo-200 text-[11px] font-bold min-w-0">
                    <BellOff size={12} className="shrink-0" />
                    <span className="truncate">
                      {muteReasonVal === "off" && "Sounds are off"}
                      {muteReasonVal === "quiet" && "Quiet hours — sounds off"}
                      {muteReasonVal === "snooze" && snoozeEnd > 0 && `Sounds off until ${formatNotifWhen(new Date(snoozeEnd), userTz)}`}
                    </span>
                  </span>
                  <button
                    onClick={resumeSounds}
                    className="shrink-0 text-emerald-300 hover:text-emerald-200 text-[11px] font-bold"
                    data-testid="notif-resume-sounds"
                  >
                    Turn on
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setSnoozeMenu((v) => !v)}
                  className="flex items-center gap-1.5 text-white/50 hover:text-white text-[11px] font-bold transition-colors"
                  data-testid="notif-snooze-btn"
                >
                  <Volume2 size={12} /> Mute sounds for…
                </button>
              )}
              {snoozeMenu && !muted && (
                <div className="absolute left-4 top-full mt-1 z-20 rounded-xl bg-slate-800 border border-white/15 shadow-xl py-1 w-40" data-testid="notif-snooze-menu">
                  {[{ h: 2, l: "2 hours" }, { h: 12, l: "12 hours" }, { h: 24, l: "24 hours" }].map((o) => (
                    <button
                      key={o.h}
                      onClick={() => doSnooze(o.h)}
                      className="w-full text-left px-3 py-2 text-xs font-bold text-white/80 hover:bg-white/10"
                      data-testid={`notif-snooze-${o.h}h`}
                    >
                      {o.l}
                    </button>
                  ))}
                  <div className="px-3 pt-1.5 pb-0.5 border-t border-white/10 mt-1">
                    <span className="text-white/35 text-[10px]">Resets automatically after.</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {selectMode && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/8 bg-white/[0.02]" data-testid="notif-select-bar">
              <button onClick={toggleSelectAll} className="text-white/70 hover:text-white text-[11px] font-bold flex items-center gap-1.5" data-testid="notif-select-all">
                <span className={`w-4 h-4 rounded border flex items-center justify-center ${allChecked ? "bg-blue-500 border-blue-500" : "border-white/30"}`}>{allChecked && <Check size={11} className="text-white" />}</span>
                Select all
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={deleteChecked}
                  disabled={checkedIds.size === 0}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-500/20 text-red-300 border border-red-500/30 text-[10px] font-bold disabled:opacity-40 hover:bg-red-500/30 transition-all"
                  data-testid="notif-delete-selected"
                >
                  <Trash2 size={11} /> Delete{checkedIds.size ? ` (${checkedIds.size})` : ""}
                </button>
                <button
                  onClick={deleteAll}
                  className="px-2.5 py-1 rounded-lg bg-white/5 text-white/60 border border-white/10 text-[10px] font-bold hover:bg-white/10 transition-all"
                  data-testid="notif-delete-all"
                >
                  Delete all
                </button>
              </div>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell size={24} className="mx-auto text-white/15 mb-2" />
                <p className="text-white/30 text-xs">No notifications yet</p>
              </div>
            ) : (
              (() => {
                let lastBucket = null;
                return shownNotifs.map((notif) => {
                const NIcon = NOTIF_ICONS[notif.icon] || Bell;
                const isChecked = checkedIds.has(notif.id);
                const bucket = dayBucket(notif.created_at, userTz);
                const showHeader = !selectMode && bucket !== lastBucket;
                lastBucket = bucket;
                return (
                  <React.Fragment key={notif.id}>
                  {showHeader && (
                    <div
                      className="sticky top-0 z-10 px-4 py-1.5 bg-slate-900/95 backdrop-blur-sm border-b border-white/5 text-[10px] font-extrabold uppercase tracking-wider text-white/40"
                      data-testid={`notif-group-${bucket.toLowerCase()}`}
                    >
                      {bucket}
                    </div>
                  )}
                  <button
                    onClick={() => (selectMode ? toggleChecked(notif.id) : openNotif(notif))}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 transition-all cursor-pointer hover:bg-white/[0.04] ${!notif.read ? "bg-white/[0.02]" : ""} ${isChecked ? "bg-blue-500/10" : ""}`}
                    data-testid={`notif-${notif.id}`}
                  >
                    {selectMode && (
                      <span className={`mt-1.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isChecked ? "bg-blue-500 border-blue-500" : "border-white/30"}`} data-testid={`notif-check-${notif.id}`}>
                        {isChecked && <Check size={11} className="text-white" />}
                      </span>
                    )}
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: `${notif.color || "#3B82F6"}15` }}
                    >
                      <NIcon size={14} style={{ color: notif.color || "#3B82F6" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white text-xs font-bold truncate">{notif.title}</p>
                        {!notif.read && <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" data-testid={`notif-unread-${notif.id}`} />}
                      </div>
                      <p className="text-white/40 text-[10px] leading-relaxed line-clamp-2">{notif.message}</p>
                      {notif.created_at && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedTimes((prev) => {
                              const s = new Set(prev);
                              if (s.has(notif.id)) s.delete(notif.id); else s.add(notif.id);
                              return s;
                            });
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); } }}
                          title="Tap to see the exact date & time"
                          className="inline-block text-white/30 hover:text-white/55 text-[9px] mt-1 font-medium cursor-pointer transition-colors"
                          data-testid={`notif-time-${notif.id}`}
                        >
                          {expandedTimes.has(notif.id)
                            ? formatNotifWhen(notif.created_at, userTz)
                            : relativeTime(notif.created_at)}
                        </span>
                      )}
                    </div>
                    {!selectMode && <ChevronLeft size={14} className="text-white/20 rotate-180 shrink-0 mt-1.5" />}
                  </button>
                  </React.Fragment>
                );
              });
              })()
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-white/8 text-center">
              <button
                onClick={() => { navigate("/notifications"); setOpen(false); exitSelectMode(); }}
                className="text-blue-400/70 hover:text-blue-400 text-[11px] font-bold transition-colors"
                data-testid="view-all-notifs"
              >
                See all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {open && selected && (
        <div className={panelClass} style={{ backgroundColor: "rgba(15, 23, 42, 0.99)" }} data-testid="notification-detail">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <button
              onClick={() => setSelected(null)}
              className="flex items-center gap-1 text-white/60 hover:text-white text-xs font-bold transition-colors"
              data-testid="notif-detail-back"
            >
              <ChevronLeft size={16} /> Back
            </button>
            <button
              onClick={() => { setOpen(false); setSelected(null); }}
              aria-label="Close"
              data-testid="notif-detail-close"
              className="w-6 h-6 rounded-full bg-white/5 text-white/50 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X size={14} />
            </button>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-3">
              {(() => { const NIcon = NOTIF_ICONS[selected.icon] || Bell; return (
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${selected.color || "#3B82F6"}20` }}>
                  <NIcon size={20} style={{ color: selected.color || "#3B82F6" }} />
                </div>
              ); })()}
              <h3 className="text-white text-base font-fredoka font-extrabold leading-tight">{selected.title}</h3>
            </div>
            {selected.created_at && (
              <p className="text-white/40 text-[11px] font-medium mb-3" data-testid="notif-detail-time">
                Received {formatNotifWhen(selected.created_at, userTz)}
              </p>
            )}
            <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line" data-testid="notif-detail-message">
              {selected.body || selected.message}
            </p>
            {selected.action_url && (
              <button
                onClick={() => goToAction(selected)}
                className="mt-5 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-blue-500 hover:brightness-110 text-white text-sm font-extrabold transition-all"
                data-testid="notif-detail-action"
              >
                Open <ArrowRight size={15} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
